import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseWidget } from './BaseWidget'
import { MessageSquareIcon, ArrowRightIcon, CheckCircleIcon } from '../Icons'
import { API_BASE_URL } from '../../utils/api'

interface FeedbackWidgetProps {
  className?: string
  widgetId?: string
}

const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({ className, widgetId }) => {
  const { t } = useTranslation()
  const [feedback, setFeedback] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!feedback.trim()) {
      setError(t('dashboard.widgets.feedbackRequired'))
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        setError(t('dashboard.widgets.feedbackAuthError'))
        return
      }

      const response = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feedback_text: feedback.trim(),
        }),
      })

      if (response.ok) {
        setShowSuccess(true)
        setFeedback('')
        
        // Hide success message after 3 seconds
        setTimeout(() => {
          setShowSuccess(false)
        }, 3000)
      } else {
        const data = await response.json()
        setError(data.error || t('dashboard.widgets.feedbackError'))
      }
    } catch (err) {
      console.error('Error submitting feedback:', err)
      setError(t('dashboard.widgets.feedbackError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <BaseWidget
      title={t('dashboard.widgets.feedback')}
      icon={<MessageSquareIcon className="h-5 w-5" />}
      accent="announcements"
      variant="gradient"
      className={className}
    >
      <form onSubmit={handleSubmit} className="h-full flex flex-col space-y-3">
        {/* Feedback Text Area */}
        <div className="flex-1 min-h-0">
          <textarea
            value={feedback}
            onChange={(e) => {
              setFeedback(e.target.value)
              setError(null)
            }}
            placeholder={t('dashboard.widgets.feedbackPlaceholder')}
            className="w-full h-full min-h-[80px] p-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 resize-none"
            disabled={isSubmitting}
            maxLength={2000}
          />
        </div>

        {/* Character Counter */}
        <div className="flex items-center justify-between text-xs text-white/70">
          <span>{feedback.length}/2000 {t('dashboard.widgets.characters')}</span>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-2 rounded-lg bg-red-500/20 border border-red-400/30">
            <p className="text-xs text-white">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {showSuccess && (
          <div className="p-2 rounded-lg bg-green-500/20 border border-green-400/30 flex items-center space-x-2">
            <CheckCircleIcon className="h-4 w-4 text-white" />
            <p className="text-xs text-white">{t('dashboard.widgets.feedbackSuccess')}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting || !feedback.trim()}
          className="w-full bg-white/20 hover:bg-white/30 disabled:bg-white/10 disabled:cursor-not-allowed text-white border border-white/30 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              <span>{t('dashboard.widgets.submitting')}</span>
            </>
          ) : (
            <>
              <ArrowRightIcon className="h-4 w-4" />
              <span>{t('dashboard.widgets.sendFeedback')}</span>
            </>
          )}
        </button>

        {/* Helper Text */}
        <p className="text-xs text-white/60 text-center">
          {t('dashboard.widgets.feedbackHelper')}
        </p>
      </form>
    </BaseWidget>
  )
}

export default FeedbackWidget

